/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Tab, Tabs } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { ContentContext, GoogleMapContext, SnackbarContext } from '../../../../contexts';
import Handler from '../../../../Handler';
import { useLabel } from '../../../../hooks';
import { useTranslationArea } from '../../../../options';
import { buttonStyle } from '../../../../styles/style';
import { scrollToFirstError } from '../../../../utils/forms';
import { useProject } from '../../hooks';
import { FinalViewer } from '../../viewer/FinalViewer';
import { projectInitialValues } from '../form';
import { BizInfo } from './BizInfo';
import { CarbonEstimateInfo } from './CarbonEstimateInfo';
import { ManagerInfo } from './ManagerInfo';
import { MonitoringPlan } from './MonitoringPlan';

export const ProjectForm = (props) => {
  const { project, setActionOpen, setSelectedAction, setSelectedNextAction } = props;
  const { slug } = useParams();
  const history = useHistory();
  const { postProject, putProject, getProject } = useProject();
  const { t } = useTranslation();
  const { ERROR } = useLabel();
  const { getSubAreaMenus } = useTranslationArea();
  const { methodologyInfo, selectableMethodologyInfo } = useContext(ContentContext);
  const { updateSnackbar } = useContext(SnackbarContext);
  const { setAddress } = useContext(GoogleMapContext);
  const [tabIndex, setTabIndex] = useState(0);
  const [googleAddress, setGoogleAddress] = useState(null);
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: projectInitialValues,
  });

  const { area, subArea, methodologySeq } = watch();
  const [updatedEvidence, setUpdatedEvidence] = useState([]);
  const methodologyTermAgreeMgs = ERROR.REQUIRED_TERM;

  /* 세부분야 목록 */
  const subAreaMenus = useMemo(() => {
    return getSubAreaMenus(area);
  }, [area]);

  /* 방법론 목록 — 신규 선택은 공개 방법론만 노출하되(ITS-91), 이미 선택돼 있는 값(미공개
   * 방법론일 수 있음, 예: 보완 재제출)은 목록에서 빠지면 FormSelect가 공란으로 렌더링되므로
   * selectable 목록에 없을 때만 methodologyInfo(전건)에서 찾아 합성한다. */
  const methodologyMenus = useMemo(() => {
    const selectableMenus = Handler.buildMethodologyMenus(selectableMethodologyInfo, area, subArea);
    const hasCurrentMethodology = selectableMenus.some((el) => el.value === Number(methodologySeq));
    if (methodologySeq && !hasCurrentMethodology) {
      const currentMethodology = Handler.getMethodologyBySeq(methodologyInfo, methodologySeq);
      if (currentMethodology) {
        const currentMenu = Handler.buildMethodologyMenus(
          [currentMethodology],
          currentMethodology.area,
          currentMethodology.subArea,
        );
        return [...selectableMenus, ...currentMenu];
      }
    }
    return selectableMenus;
  }, [selectableMethodologyInfo, methodologyInfo, area, subArea, methodologySeq]);

  /* 방법론 하위의 증빙서류 및 약관 목록 */
  const methodologyEvidenceAndTerms = useMemo(() => {
    if (!methodologySeq) return null;
    const methodology = Handler.getMethodologyBySeq(methodologyInfo, methodologySeq);
    return {
      // ITS-186. 방법론 응답의 새 이름(terms/evidence)을 쓴다.
      evidence: methodology?.evidence,
      terms: methodology?.terms,
    };
  }, [area, subArea, methodologySeq]);

  /* 이전으로 이동 */
  const handlePrev = () => {
    if (tabIndex > 0) {
      setTabIndex(tabIndex - 1);
    } else {
      history.push('/project/list');
    }
  };

  /* 다음으로 이동 */
  const goToNext = () => {
    getProject();
    setTabIndex(tabIndex + 1);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  /* Form Request 요청 */
  const onSubmit = useCallback(
    async (data, shouldGoNext = true) => {
      /* STEP 0,1 단계일 경우는 API 요청 */
      if (methodologyEvidenceAndTerms?.terms?.length > 0 && !data.isTermsAgree) {
        updateSnackbar({
          type: 'error',
          open: true,
          message: methodologyTermAgreeMgs,
        });
        return;
      }
      if (slug) {
        /* STEP 3: 최종 확인 탭일 경우 */
        if (tabIndex === 2 && shouldGoNext) {
          /* Action 실행: 최종 저장 후 Document 상태 변경 */
          setSelectedAction({
            seq: slug,
          });
          const nextAction = project.type === 'Improve' ? 'ImproveProjectComplete' : 'RequestProjectConfirm';
          setSelectedNextAction(nextAction);
          setActionOpen(true);
          return;
        } else {
          /* STEP 2: 온실가스 예상 감축량 & 기타 증빙자료 탭일 경우 */
          if (tabIndex === 1) {
            /* 필수첨부 증빙자료 */
            const requiredEvidence = methodologyEvidenceAndTerms?.evidence?.filter((el) => el.isForce);
            const requiredEvidenceSeq = requiredEvidence?.map((el) => el?.evidence?.seq);

            /* 현재 데이터 증빙자료 */
            const dataEvidence = data?.evidence
              ?.filter((el) => requiredEvidenceSeq?.includes(el.seq))
              ?.map((el) => el.seq);

            if (dataEvidence?.some((el) => !el.data)) {
              updateSnackbar({
                type: 'error',
                open: true,
                message: ERROR['CHECK_SUPPORT_DOC'],
              });
              return;
            }
          }
          await putProject(data);
          if (shouldGoNext && tabIndex < 2) {
            goToNext();
          }
        }
      } else {
        postProject(data);
        if (shouldGoNext && tabIndex < 2) {
          goToNext();
        }
      }
    },
    [slug, methodologyEvidenceAndTerms, methodologyTermAgreeMgs, putProject, tabIndex, postProject, updateSnackbar],
  );

  const onError = (errors, event) => {
    if (event) {
      event.preventDefault();
    }
    scrollToFirstError(errors);
  };

  const nextLabel = useMemo(() => {
    if (tabIndex === 2) {
      return project?.type === 'Improve' ? t('COMPLEMENT_APPLY') : t('PROJECT_SUBMIT');
    }
    return t('TEMP_SAVE_NEXT');
  }, [tabIndex, project?.type, t]);

  useEffect(() => {
    if (project && methodologyInfo) {
      const methodology = Handler.getMethodologyBySeq(methodologyInfo, project?.project?.methodology?.seq);
      const location = Handler.jsonParser(project?.location);
      setUpdatedEvidence(
        project?.projectDocEvidence?.length > 0
          ? project?.projectDocEvidence?.map((el) => ({ seq: el.seq, data: el.data, evidenceSeq: el.evidenceSeq }))
          : [],
      );
      setGoogleAddress(location?.addr1?.formatted_address);
      reset({
        ...project,
        location: {
          addr1: location?.addr1,
          addr2: location?.addr2,
        },
        area: methodology?.area,
        subArea: methodology?.subArea,
        methodologySeq: methodology?.seq,
        evidence:
          project?.projectDocEvidence?.length > 0
            ? project?.projectDocEvidence?.map((el) => ({ seq: el.seq, data: el.data, 
             }))
            : [],
        reductionStartDate: dayjs(project.reductionStartDate),
        reductionEndDate: dayjs(project.reductionEndDate),
        startDate: dayjs(project.startDate),
        endDate: dayjs(project.endDate),
        exportedDate: dayjs(project.exportedDate),
        exportedTitle: project.exportedTitle,
        exportedOrgan: project.exportedOrgan,
      });
    }
  }, [project, methodologyInfo]);

  useEffect(() => {
    if (!slug) {
      setAddress('');
    }
  }, []);

  return (
    <div>
      <article className='mb-6 border-b border-gray-200 pb-6'>
        <ul className='flex flex-col gap-2'>
          <li className='grid grid-cols-[120px_1fr] items-center gap-2'>
            <header>
              <span className='font-bold text-sm'>{t('BIZ_START_DATE')}</span>
            </header>
            <span className='font-normal text-sm'>{t('BIZ_START_DATE_MSG')}</span>
          </li>
          <li className='grid grid-cols-[120px_1fr] items-center gap-2'>
            <header>
              <span className='font-bold text-sm'>{t('CERT_START_DATE')}</span>
            </header>
            <span className='font-normal text-sm'>{t('BIZ_START_DATE_MSG')}</span>
          </li>
          <li className='grid grid-cols-[120px_1fr] items-center gap-2'>
            <header>
              <span className='font-bold text-sm'>{t('CERT_END_DATE')}</span>
            </header>
            <span className='font-normal text-sm'>{t('BIZ_START_DATE') + ' ' + t('VALID_PERIOD')}</span>
          </li>
        </ul>
      </article>
      <form
        id='project-form'
        noValidate
        onSubmit={(e) => {
          // 브라우저 기본 동작 방지를 위해 preventDefault 먼저 호출
          handleSubmit(onSubmit, onError)(e);
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(event, newValue) => setTabIndex(newValue)}
          sx={{
            'width': '100%',
            '& .MuiTabs-flexContainer': {
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              borderBottom: '1px solid #e0e0e0',
            },
          }}
        >
          <Tab label={t('OVERVIEW')} value={0} onClick={(e) => e.preventDefault()} />
          <Tab label={t('ESTIMATED_DOCUMENT')} disabled={!slug} value={1} onClick={(e) => e.preventDefault()} />
          <Tab label={t('FINAL')} disabled={!slug} value={2} onClick={(e) => e.preventDefault()} />
        </Tabs>
        <div
          role='tabpanel'
          id={`simple-tabpanel-${tabIndex}`}
          aria-labelledby={`simple-tab-${tabIndex}`}
          className='w-[945px]'
        >
          {tabIndex === 0 && (
            <Box
              sx={{
                pt: 4,
              }}
            >
              <ManagerInfo
                register={register}
                errors={errors}
                control={control}
                subAreaMenus={subAreaMenus}
                methodologyMenus={methodologyMenus}
                watch={watch}
                setValue={setValue}
              />
              <BizInfo
                register={register}
                errors={errors}
                control={control}
                googleAddress={googleAddress}
                setGoogleAddress={setGoogleAddress}
                subAreaMenus={subAreaMenus}
                methodologyMenus={methodologyMenus}
                methodologyEvidenceAndTerms={methodologyEvidenceAndTerms}
                watch={watch}
                setValue={setValue}
              />
            </Box>
          )}
          {tabIndex === 1 && (
            <Box
              sx={{
                pt: 4,
              }}
            >
              <CarbonEstimateInfo
                register={register}
                errors={errors}
                control={control}
                watch={watch}
                setValue={setValue}
              />
              <MonitoringPlan
                register={register}
                errors={errors}
                control={control}
                watch={watch}
                updatedEvidence={updatedEvidence}
                evidenceList={methodologyEvidenceAndTerms?.evidence}
              />
            </Box>
          )}
          {tabIndex === 2 && (
            <Box
              sx={{
                pt: 4,
              }}
            >
              <FinalViewer project={project} />
            </Box>
          )}
        </div>
        <article className='flex justify-between w-full gap-8'>
          <Button variant='outlined' sx={buttonStyle} onClick={handlePrev}>
            {t('PREV')}
          </Button>
          <div className='flex items-center gap-2'>
            <Button
              variant='outlined'
              color='primary'
              type='button'
              form='project-form'
              sx={buttonStyle}
              onClick={() => {
                handleSubmit((data) => onSubmit(data, false), onError)();
              }}
            >
              임시저장
            </Button>
            <Button
              variant='contained'
              disableElevation
              color='primary'
              sx={buttonStyle}
              type='submit'
              form='project-form'
            >
              {nextLabel}
            </Button>
          </div>
        </article>
      </form>
    </div>
  );
};
